import {
  Avatar,
  Box,
  Grow,
  Slide,
  Stack,
  Typography,
  keyframes,
  typographyClasses,
  useTheme,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { Achievement } from '../types';
import './styles.css';
import './AchievementAnimation.css';
import styled from '@emotion/styled';

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

const shine = keyframes`
  0% {
    background-position: -500px;
  }
  100% {
    background-position: 500px;
  }
`;

const ShinyTypography = styled(Typography)(() => ({
  [`&.${typographyClasses.root}`]: {
    animation: `${shine} 4s infinite`,
    backgroundClip: 'text',
    backgroundImage:
      'linear-gradient(-40deg, transparent 0%, transparent 40%, #fff 50%, transparent 60%, transparent 100%)',
    backgroundRepeat: 'no-repeat',
  },
}));

type PropTypes = {
  detailedAchievements: Achievement[];
  show: boolean;
};

export const AchievementAnimation = ({
  detailedAchievements,
  show,
}: PropTypes) => {
  const theme = useTheme();
  const [timedShow, setTimedShow] = useState(false);
  const [displayedAchievement, setDisplayedAchievement] =
    useState<Achievement>();
  const [slideTitle, setSlideTitle] = useState(false);
  const [slideDisplayName, setSlideDisplayName] = useState(false);
  const [slideDescription, setSlideDescription] = useState(false);
  const containerRef = React.useRef(null);

  useEffect(() => {
    if (!show) return;
    cycleThroughAchivements(detailedAchievements);
  }, [show]);

  const cycleThroughAchivements = async (
    detailedAchievements: Achievement[]
  ) => {
    for (const achievement of detailedAchievements) {
      if (!show) return;
      await showThenHideAchievement(achievement);
      await sleep(1000);
      setSlideTitle(false);
      setSlideDisplayName(false);
      setSlideDescription(false);
    }
  };

  const showThenHideAchievement = async (achievement: Achievement) => {
    setDisplayedAchievement(achievement);
    setTimedShow(true);
    setTimeout(() => {
      setSlideTitle(true);
    }, 200);
    setTimeout(() => {
      setSlideDisplayName(true);
    }, 400);
    setTimeout(() => {
      setSlideDescription(true);
    }, 600);
    await sleep(4000);
    setTimedShow(false);
  };

  return (
    <>
      {displayedAchievement ? (
        <Grow in={timedShow} timeout={500}>
          <Box
            className="gradient-border"
            ref={containerRef}
            sx={{
              display: 'flex',
              alignItems: 'center',
              position: 'absolute',
              top: '75%',
              left: 'calc(50% - 300px)',
              transform: 'translate(-50%, -50%)',
              zIndex: 999999,
              width: '600px',
            }}
          >
            <Stack
              direction="row"
              sx={{
                padding: 2,
                background: theme.palette.background.default,
                width: '600px',
                borderRadius: '80px',
              }}
            >
              <Avatar
                src={displayedAchievement.imageUrl}
                sx={{
                  height: 100,
                  width: 100,
                }}
              />
              {slideTitle ? (
                <Stack spacing={1} sx={{ paddingLeft: 4, paddingRight: 2 }}>
                  <Slide
                    in={slideTitle}
                    direction="left"
                    timeout={250}
                    container={containerRef.current}
                  >
                    <ShinyTypography
                      variant="h6"
                      fontWeight={600}
                      sx={{ color: theme.palette.primary.main }}
                    >
                      Achievement Awarded!
                    </ShinyTypography>
                  </Slide>
                  <Slide
                    in={slideDisplayName}
                    direction="left"
                    timeout={250}
                    container={containerRef.current}
                  >
                    <ShinyTypography fontWeight={600}>
                      {displayedAchievement.displayName}
                    </ShinyTypography>
                  </Slide>
                  <Slide
                    in={slideDescription}
                    direction="left"
                    timeout={250}
                    container={containerRef.current}
                  >
                    <ShinyTypography variant="caption">
                      {displayedAchievement.condition}
                    </ShinyTypography>
                  </Slide>
                </Stack>
              ) : null}
            </Stack>
          </Box>
        </Grow>
      ) : null}
    </>
  );
};
